import {Helmet} from '@dr.pogodin/react-helmet';
import {BreadcrumbType, Form, FormField, FormSubmitHandler} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {object} from 'yup';

import {useParams} from 'react-router-dom';

import {
  useGetConfigurationAuthorizationOperationIdQuery,
  useGetConfigurationAuthorizationsQuery,
  useGetOperationItemsQuery,
  usePatchConfigurationAuthorizationOperationIdMutation,
  usePostConfigurationAuthorizationOperationIdMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate, yupString} from 'shared/web';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('page.accounting.labels.operationAuthorizationsOverview'),
    href: settingsRoutes.accountingOperationAuthorizationsOverview,
  },
];

type FormValues = {
  authorizationId: string;
  operationCode: string;
};

export function OperationAuthorizationDetail() {
  const navigate = useNavigate();
  const {id: recordId} = useParams();
  const isCreating = recordId === 'new';

  const title = isCreating
    ? i18n.t('page.accounting.labels.operationAuthorizationNew')
    : i18n.t('page.accounting.labels.operationAuthorizationUpdate');

  const {data: authorizationOperationData, isLoading: isLoadingAuthorization} =
    useGetConfigurationAuthorizationOperationIdQuery({id: recordId ?? ''}, {skip: isCreating});

  const [createAuthorization, {isLoading: isLoadingPostAuthorization}] =
    usePostConfigurationAuthorizationOperationIdMutation();
  const [updateAuthorization, {isLoading: isLoadingPatchAuthorization}] =
    usePatchConfigurationAuthorizationOperationIdMutation();

  const {data: authorizations} = useGetConfigurationAuthorizationsQuery();

  const {data: dictionary} = useGetOperationItemsQuery({
    application: 'workshop',
  });

  const authorizationsOptions =
    authorizations
      ?.filter((item) => item?.id != null && item?.name != null)
      .map((item) => ({
        value: item?.id,
        label: item?.name,
      })) ?? [];

  const operationOptions =
    dictionary
      ?.filter((item) => item.operationId != null && item.itemCodeName != null)
      .map((item) => ({
        value: item.operationId,
        label: `${item.operationId} ${item.itemCodeName}`,
      })) ?? [];

  const isLoading = isLoadingPostAuthorization || isLoadingPatchAuthorization;

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const mutation = isCreating
      ? createAuthorization({
          body: {
            operationId: data.operationCode ?? null,
            authorizationId: data.authorizationId ?? null,
          },
        })
      : updateAuthorization({
          id: recordId ?? null,
          operationId: data.operationCode ?? null,
          authorizationId: data.authorizationId ?? null,
        });

    await mutation
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.accountingOperationAuthorizationsOverview);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues: Partial<FormValues> = {
    authorizationId: authorizationOperationData?.authorizationId ?? '',
    operationCode: authorizationOperationData?.operationId ?? '',
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          breadcrumbs,
        }}
        isLoading={isLoadingAuthorization}
        data-testid={testIds.settings.accountingOperationAuthorizationDetail('page')}
      >
        <Form<FormValues>
          onSubmit={handleSubmit}
          schema={schema}
          defaultValues={defaultValues}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <VStack spacing={4}>
              <SettingsSection>
                <Grid columns={2} align="flex-end">
                  <FormField
                    control={control}
                    type="choice"
                    options={operationOptions}
                    name="operationCode"
                    label={i18n.t('page.accounting.labels.operationAuthorization.operationId')}
                    isRequired
                    data-testid={testIds.settings.accountingOperationAuthorizationDetail(
                      'operationId'
                    )}
                  />
                  <FormField
                    control={control}
                    type="choice"
                    name="authorizationId"
                    options={authorizationsOptions}
                    isNotClearable
                    label={i18n.t('page.accounting.labels.operationAuthorization.authorization')}
                    isRequired
                    data-testid={testIds.settings.accountingOperationAuthorizationDetail(
                      'authorizationId'
                    )}
                  />
                </Grid>
              </SettingsSection>
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    onClick: () =>
                      navigateWithoutBlocker(
                        settingsRoutes.accountingOperationAuthorizationsOverview
                      ),
                    variant: 'secondary',
                  },
                  {
                    type: 'form-button',
                    control,
                    isLoading,
                    buttonType: 'submit',
                    title: i18n.t('general.actions.saveChanges'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}

const schema = object({
  authorizationId: yupString.required(),
  operationCode: yupString.required(),
});
